/**
 * Consolidated API Client
 * Combines functionality from apiClient.js, globalApiClient.js, and apiConfig.js
 * Provides enhanced API client with error handling, retries, caching, and global instance management
 */

// Import individual utilities to avoid circular dependency
import { fetchWithRetry, errorReporting } from './consolidatedErrorHandling.js';
import { parseApiError, parseNetworkError, StandardizedError } from './consolidatedErrorHandling.js';
import { ERROR_CATEGORIES, ERROR_SEVERITY } from './consolidatedErrorHandling.js';
import performanceMonitor from './performanceMonitor.js';
import requestDeduplication from './requestDeduplication.js';
import cacheManager from './cacheManager.js';

// Get the API base URL from environment variable or default to relative URLs for local development
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Get the full API URL for a given endpoint
 * @param {string} endpoint - The API endpoint (e.g., '/api/admin/users')
 * @returns {string} The full API URL
 */
export const getApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};

/**
 * Enhanced API client with error handling, retries, and loading states
 */
class ApiClient {
  constructor(getToken, onAuthError = null) {
    this.getToken = getToken;
    this.onAuthError = onAuthError;
    this.baseURL = API_BASE_URL;
    this.defaultTimeout = 30000;
    
    // Request-specific timeout configurations
    this.timeoutConfigs = {
      'wizard-data': 45000,    // 45 seconds for wizard data requests
      'upload': 60000,         // 60 seconds for file uploads
      'default': 30000         // 30 seconds default
    };
    
    // Request-specific retry configurations
    this.retryConfigs = {
      'wizard-data': {
        maxRetries: 3,
        baseDelay: 2000,
        maxDelay: 8000,
        retryableStatuses: [429, 500, 502, 503, 504],
        retryableErrors: ['network', 'timeout', 'abort']
      },
      'default': {
        maxRetries: 3,
        baseDelay: 1000,
        maxDelay: 10000,
        retryableStatuses: [429, 500, 502, 503, 504],
        retryableErrors: ['network', 'timeout']
      }
    };
    
    // Active request controllers for cancellation support
    this.activeRequests = new Map();
    
    // Request interceptors
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    
    // Performance monitoring
    this.enablePerformanceMonitoring = true;
    
    // Cache configuration
    this.cacheConfig = {
      enabled: true,
      defaultTTL: 5 * 60 * 1000, // 5 minutes
      maxSize: 100
    };
  }

  /**
   * Invalidate cached response for an endpoint
   * @param {string} endpoint - API endpoint (e.g., '/api/knowledge-base')
   */
  invalidateCache(endpoint) {
    try {
      const key = getApiUrl(endpoint);
      cacheManager.delete(key);
    } catch (_) {
      // no-op
    }
  }

  /**
   * Invalidate multiple cache keys by endpoints
   * @param {string[]} endpoints - Array of endpoints to clear
   */
  invalidateCaches(endpoints = []) {
    endpoints.forEach((ep) => this.invalidateCache(ep));
  }

  /**
   * Add request interceptor
   * @param {Function} interceptor - Function to modify request before sending
   */
  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   * @param {Function} interceptor - Function to modify response after receiving
   */
  addResponseInterceptor(interceptor) {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Get timeout for request type
   * @param {string} requestType - Type of request
   * @returns {number} Timeout in milliseconds
   */
  getTimeout(requestType = 'default') {
    return this.timeoutConfigs[requestType] || this.timeoutConfigs.default;
  }

  /**
   * Get retry configuration for request type
   * @param {string} requestType - Type of request
   * @returns {object} Retry configuration
   */
  getRetryConfig(requestType = 'default') {
    return this.retryConfigs[requestType] || this.retryConfigs.default;
  }

  /**
   * Make authenticated API request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Request options
   * @param {string} requestType - Type of request for timeout/retry config
   * @returns {Promise<any>} Response data
   */
  async request(endpoint, options = {}, requestType = 'default') {
    const requestId = `${endpoint}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullUrl = getApiUrl(endpoint);
    
    try {
      // Start performance monitoring
      if (this.enablePerformanceMonitoring) {
        performanceMonitor.startTiming(requestId, endpoint, options.method || 'GET');
      }

      // Create the actual request function
      const makeRequest = async () => {
        // Check cache first
        if (options.method === 'GET' && this.cacheConfig.enabled) {
          const cachedResponse = cacheManager.get(fullUrl);
          if (cachedResponse) {
            if (this.enablePerformanceMonitoring) {
              performanceMonitor.endTiming(requestId, { cached: true, success: true });
            }
            return cachedResponse;
          }
        }

        // Get authentication token
        const token = await this.getToken();
        
        // Prepare request options
        const requestOptions = {
          ...options,
          headers: {
            'Authorization': `Bearer ${token}`,
            ...options.headers
          },
          timeout: this.getTimeout(requestType)
        };

        // Only set Content-Type for non-FormData requests
        if (!(options.body instanceof FormData)) {
          requestOptions.headers['Content-Type'] = 'application/json';
        }

        // Apply request interceptors
        for (const interceptor of this.requestInterceptors) {
          await interceptor(requestOptions);
        }

        // Create abort controller for this request
        const controller = new AbortController();
        this.activeRequests.set(requestId, controller);

        // Make request with retry logic
        const retryConfig = this.getRetryConfig(requestType);
        const response = await fetchWithRetry(fullUrl, {
          ...requestOptions,
          signal: controller.signal
        }, retryConfig.maxRetries);

        // Remove from active requests
        this.activeRequests.delete(requestId);

        // Handle non-ok responses
        if (!response.ok) {
          const error = await parseApiError(response);
          
          // Handle authentication errors
          if (response.status === 401 && this.onAuthError) {
            this.onAuthError(error);
          }
          
          // Report error
          errorReporting.reportError(error, {
            endpoint,
            requestType,
            requestId
          });
          
          throw error;
        }

        // Parse response
        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          data = await response.text();
        }

        // Apply response interceptors
        for (const interceptor of this.responseInterceptors) {
          data = await interceptor(data, response);
        }

        // Handle cache invalidation instructions from backend
        if (data.cacheInvalidation) {
          if (data.cacheInvalidation.clearAll) {
            cacheManager.clear();
            console.log('[ApiClient] Cleared all caches per backend instruction');
          } else if (data.cacheInvalidation.endpoints) {
            this.invalidateCaches(data.cacheInvalidation.endpoints);
            console.log('[ApiClient] Invalidated caches per backend instruction:', data.cacheInvalidation.endpoints);
          }
        }

        // Cache GET responses
        if (options.method === 'GET' && this.cacheConfig.enabled) {
          cacheManager.set(fullUrl, data, this.cacheConfig.defaultTTL);
        }

        // End performance monitoring
        if (this.enablePerformanceMonitoring) {
          performanceMonitor.endTiming(requestId, {
            status: response.status,
            size: JSON.stringify(data).length,
            success: true
          });
        }

        return data;
      };

      // Use request deduplication for GET requests
      if (options.method === 'GET') {
        const deduplicationKey = requestDeduplication.generateKey(endpoint, 'GET', options);
        return await requestDeduplication.deduplicateRequest(deduplicationKey, makeRequest);
      } else {
        // For non-GET requests, execute directly
        return await makeRequest();
      }

    } catch (error) {
      // Remove from active requests
      this.activeRequests.delete(requestId);

      // End performance monitoring with error
      if (this.enablePerformanceMonitoring) {
        performanceMonitor.endTiming(requestId, { error: error.message, success: false });
      }

      // Parse and standardize error
      let standardizedError;
      if (error instanceof StandardizedError) {
        standardizedError = error;
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        standardizedError = parseNetworkError(error);
      } else {
        standardizedError = new StandardizedError({
          message: error.message || 'Request failed',
          code: 'REQUEST_ERROR',
          category: ERROR_CATEGORIES.SYSTEM,
          severity: ERROR_SEVERITY.MEDIUM,
          originalError: error
        });
      }

      // Report error
      errorReporting.reportError(standardizedError, {
        endpoint,
        requestType,
        requestId
      });

      throw standardizedError;
    }
  }

  /**
   * Cancel all active requests
   */
  cancelAllRequests() {
    for (const [requestId, controller] of this.activeRequests) {
      controller.abort();
      this.activeRequests.delete(requestId);
    }
  }

  /**
   * Cancel specific request
   * @param {string} requestId - Request ID to cancel
   */
  cancelRequest(requestId) {
    const controller = this.activeRequests.get(requestId);
    if (controller) {
      controller.abort();
      this.activeRequests.delete(requestId);
    }
  }

  // Convenience methods
  async get(endpoint, options = {}, requestType = 'default') {
    return this.request(endpoint, { ...options, method: 'GET' }, requestType);
  }

  async post(endpoint, data = null, options = {}, requestType = 'default') {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined)
    }, requestType);
  }

  async put(endpoint, data = null, options = {}, requestType = 'default') {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined)
    }, requestType);
  }

  async patch(endpoint, data = null, options = {}, requestType = 'default') {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined)
    }, requestType);
  }

  async delete(endpoint, options = {}, requestType = 'default') {
    return this.request(endpoint, { ...options, method: 'DELETE' }, requestType);
  }

  // File upload method
  async upload(endpoint, formData, options = {}, onProgress = null) {
    const token = await this.getToken();
    
    const requestOptions = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...options.headers
      },
      body: formData,
      timeout: this.getTimeout('upload')
    };

    // Remove Content-Type header to let browser set it with boundary
    delete requestOptions.headers['Content-Type'];

    return this.request(endpoint, requestOptions, 'upload');
  }

  /**
   * Backward-compatible: decode a JWT and try to extract a user id
   * Works with Clerk and generic JWTs. Returns null if unavailable.
   */
  extractUserIdFromToken(token) {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
      // Base64URL decode
      const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = typeof atob !== 'undefined' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
      const payload = JSON.parse(decodeURIComponent(Array.prototype.map.call(json, c =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join('')));
      return (
        payload.sub ||
        payload.user_id ||
        payload.uid ||
        payload.userId ||
        payload.clerk_user_id ||
        null
      );
    } catch (_) {
      return null;
    }
  }

  /**
   * Backward-compatible cancellable GET with optional cache-busting and deduplication.
   * Uses the provided requestId for abort management.
   */
  async cancellableGet(endpoint, options = {}, requestId, requestType = 'wizard-data') {
    const baseUrl = getApiUrl(endpoint);
    let url = baseUrl;

    // Apply cache-busting when requested
    if (options.bustCache) {
      const ts = Date.now();
      const sep = url.includes('?') ? '&' : '?';
      url += `${sep}_cb=${ts}`;
      if (options.cacheVersion) {
        url += `&v=${encodeURIComponent(options.cacheVersion)}`;
      }
    }

    const makeRequest = async () => {
      try {
        const token = await this.getToken();
        const requestOptions = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {})
          },
          timeout: options.timeout || this.getTimeout(requestType)
        };

        // Apply request interceptors
        for (const interceptor of this.requestInterceptors) {
          await interceptor(requestOptions);
        }

        // Set up abort controller mapped to supplied requestId
        const controller = new AbortController();
        if (requestId) {
          // If an existing request with same id exists, abort it first
          const existing = this.activeRequests.get(requestId);
          if (existing) {
            try { existing.abort(); } catch (_) {}
            this.activeRequests.delete(requestId);
          }
          this.activeRequests.set(requestId, controller);
        }

        const retryConfig = this.getRetryConfig(requestType);
        const response = await fetchWithRetry(url, { ...requestOptions, signal: controller.signal }, retryConfig.maxRetries);

        if (requestId) {
          this.activeRequests.delete(requestId);
        }

        if (!response.ok) {
          const error = await parseApiError(response);
          // Auth handler
          if (response.status === 401 && this.onAuthError) {
            this.onAuthError(error);
          }
          errorReporting.reportError(error, { endpoint, requestType, requestId });
          throw error;
        }

        let data;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          data = await response.text();
        }

        // Apply response interceptors
        for (const interceptor of this.responseInterceptors) {
          // Allow interceptor to transform the data
          // eslint-disable-next-line no-await-in-loop
          data = await interceptor(data, response);
        }

        // Populate cache for GET responses
        if (this.cacheConfig.enabled) {
          cacheManager.set(baseUrl, data, this.cacheConfig.defaultTTL);
        }

        return data;
      } catch (err) {
        if (requestId) {
          this.activeRequests.delete(requestId);
        }

        let standardizedError;
        if (err instanceof StandardizedError) {
          standardizedError = err;
        } else if (err.name === 'TypeError' && err.message?.includes('fetch')) {
          standardizedError = parseNetworkError(err);
        } else {
          standardizedError = new StandardizedError({
            message: err.message || 'Request failed',
            code: 'REQUEST_ERROR',
            category: ERROR_CATEGORIES.SYSTEM,
            severity: ERROR_SEVERITY.MEDIUM,
            originalError: err
          });
        }

        errorReporting.reportError(standardizedError, { endpoint, requestType, requestId });
        throw standardizedError;
      }
    };

    if (options.deduplication) {
      const dedupKey = requestDeduplication.generateKey(endpoint, 'GET', options);
      return requestDeduplication.deduplicateRequest(dedupKey, makeRequest);
    }
    return makeRequest();
  }
}

// Global API client management
let globalApiClient = null;
let authErrorHandler = null;

/**
 * Set the global authentication error handler
 * @param {Function} handler - Function to call when auth errors occur
 */
export const setGlobalAuthErrorHandler = (handler) => {
  authErrorHandler = handler;
  
  // If we already have a global client, update its handler
  if (globalApiClient) {
    globalApiClient.onAuthError = handler;
  }
};

/**
 * Create or get the global API client instance
 * @param {Function} getToken - Function to get the auth token
 * @returns {ApiClient} The global API client instance
 */
export const getGlobalApiClient = (getToken) => {
  if (!globalApiClient) {
    globalApiClient = new ApiClient(getToken, authErrorHandler);
  }
  
  return globalApiClient;
};

/**
 * Reset the global API client (useful for testing or when auth context changes)
 */
export const resetGlobalApiClient = () => {
  if (globalApiClient) {
    globalApiClient.cancelAllRequests();
  }
  globalApiClient = null;
};

/**
 * Make an API request with the correct base URL (legacy compatibility)
 * @param {string} endpoint - The API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export const apiRequest = (endpoint, options = {}) => {
  return fetch(getApiUrl(endpoint), options);
};

export { ApiClient };
export default getGlobalApiClient;
